import { useCallback } from 'react';

import type { SiteSpace } from '@gitbook/api-client';
import {
    type AllSideSheets,
    getNavigationSideSheetTabs,
    type TabKeys,
} from '@gitbook/app-navigation';

import { useNavigationState } from './NavigationProvider';
import { useNavigation } from './useNavigation';

/**
 * Handle navigation in a sidesheet. The caller must pass in the type of the sidesheet
 * to get correct typing of parameters.
 */
export function useSideSheetNavigation<SideSheet extends AllSideSheets = never>(
    // = never means the caller must define the generic
    type: SideSheet['type'],
    defaultSelectedTab?: TabKeys<SideSheet['tab']>
): {
    sideSheet: SideSheet | null;
    opened: boolean;
    close: () => void;
    selectedTab: TabKeys<SideSheet['tab']>;
    setSelectedTab: (
        sideSheetTab: TabKeys<SideSheet['tab']>,
        siteSpaceId?: SiteSpace['id']
    ) => void;
    updateSideSheet: (sideSheet: SideSheet) => void;
} {
    const { navigate } = useNavigation();
    const state = useNavigationState();
    const tabs = getNavigationSideSheetTabs(type);

    const setSelectedTab = useCallback(
        (tab: TabKeys<SideSheet['tab']>, siteSpaceId?: SiteSpace['id']) =>
            navigate(
                {
                    screen: {
                        sideSheet: {
                            // @ts-ignore
                            type,
                            // @ts-ignore
                            tab,
                            siteSpaceId,
                        },
                    },
                },
                { replace: true }
            ),
        [navigate, type]
    );

    const updateSideSheet = useCallback(
        (sideSheet: SideSheet) => {
            // @ts-expect-error
            navigate({ screen: { sideSheet } }, { replace: true });
        },
        [navigate]
    );

    const close = useCallback(() => navigate({ screen: { sideSheet: undefined } }), [navigate]);
    const abstractSideSheet =
        // sideSheet can contain modal, so we don't render them if there is an active overlay
        'sideSheet' in state.screen && state.screen.sideSheet && !state.overlay
            ? state.screen.sideSheet
            : null;

    const selectedTab =
        (abstractSideSheet && 'tab' in abstractSideSheet && abstractSideSheet.tab
            ? abstractSideSheet.tab
            : defaultSelectedTab) ?? tabs[0];

    const sideSheet = abstractSideSheet?.type === type ? (abstractSideSheet as SideSheet) : null;

    return {
        sideSheet,
        opened: Boolean(sideSheet),
        // @ts-ignore this will be the right tab
        selectedTab,
        setSelectedTab,
        updateSideSheet,
        close,
    };
}
